import { NgClass, NgStyle } from '@angular/common';
import { Component, ElementRef, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';

import * as _ from 'underscore';

import { EnvironmentConfig } from '../../../client-config/bma-env-config';
import { ICouponConfig, ISystemConfig } from '../../../core/services/cms/models/system-config';
import { DeviceService } from '../../../core/services/device/device.service';
import { DomToolsService } from '../../../core/services/domTools/dom.tools.service';
import { GtmService } from '../../../core/services/gtm/gtm.service';
import { RoutingHelperService } from '../../../core/services/routingHelper/routing-helper.service';
import { WindowRefService } from '../../../core/services/windowRef/window-ref.service';
import { Carousel } from '../../../shared/directives/ng-carousel/carousel.class';
import { NgCarouselSADirective } from '../../../shared/directives/ng-carousel/carousel.directive.sa';
import { CarouselService } from '../../../shared/directives/ng-carousel/carousel.service';
import { RendererService } from '../../../shared/services/renderer/renderer.service';
import { COUPONS, DATED_COUPONS, MATCHES } from '../couponsList/coupons-list.constant';
import { CouponsListService } from '../couponsList/coupons-list.service';
import { ICoupon, ICouponsConfig } from '../couponsListSportTab/coupons.model';
import { ICouponCarousel } from './coupons-carousel.model';

@Component({
    selector: 'coupons-carousel',
    templateUrl: 'coupons-carousel.component.html',
    styleUrls: ['coupons-carousel.component.scss'],
    standalone: true,
    imports: [NgClass, NgStyle, NgCarouselSADirective],
})
export class CouponsCarouselComponent implements OnInit, OnDestroy {
    @Input() couponsList: ICoupon[];
    isCoral: boolean;

    public slides: number = 0;

    public carouselName: string = 'coupon_carousel' + new Date().getTime();
    private readonly environment = inject(EnvironmentConfig).configuration;

    gradientCss: string;
    couponsCarouselList: ICouponCarousel[] = [];
    couponsCarouselListCopy: ICouponCarousel[];
    showCarouselButtons: boolean;
    private resizeListener: Function;
    isDesktop: boolean;
    public title: string = 'Featured coupons';

    constructor(
        private couponsListService: CouponsListService,
        private router: Router,
        private routingHelperService: RoutingHelperService,
        private carouselService: CarouselService,
        private windowRef: WindowRefService,
        private domTools: DomToolsService,
        private elementRef: ElementRef,
        private device: DeviceService,
        private renderer: RendererService,
        protected gtmService: GtmService,
    ) {
        this.isCoral = this.environment && this.environment.BRAND === 'bma';
        this.isDesktop = this.device.isDesktop;
    }

    ngOnInit(): void {
        this.couponsListService.getCouponCmsConfig().subscribe((couponConfig: ICouponConfig) => {
            if (couponConfig && couponConfig.title) {
                this.title = couponConfig.title;
            }
        });
        this.loadCouponCarousels();
        if (!this.device.isTouch()) {
            this.windowRef.nativeWindow.setTimeout(() => {
                this.initShowCarouselButtons();
            }, 0);
            this.resizeListener = this.renderer.renderer.listen(this.windowRef.nativeWindow, 'resize', () => {
                this.initShowCarouselButtons();
            });
        }
    }

    /**
     * Load Coupons Data
     */
    private loadCouponCarousels(): void {
        this.couponsListService.getCouponCarousel().subscribe(
            (carousels: ICouponCarousel[]) => {
                // filter unwanted coupon config
                carousels = carousels.filter((item) => this.couponsList.some((coupon) => coupon.id === item.couponKeys));
                const _uniqCarousels = carousels.filter(
                    (item: ICouponCarousel) => item.dateBanner + '_' + item.marketSelector + '_' + item.couponKeys,
                );
                const groupedCarousels = this.couponsListService.groupCarousel(_uniqCarousels);
                const filteredObjects = groupedCarousels.slice(0, 7);
                this.couponsCarouselList = filteredObjects.length >= 3 ? filteredObjects : [];
                const bgColor = this.isCoral ? '#084d8d,#062a4b' : '#e61c26,#a11318';
                this.couponsCarouselList.forEach((carousel, index) => {
                    const cmsBgColor = carousel?.hexCode?.split(',');
                    const gradientColors = cmsBgColor?.length === 2 ? cmsBgColor : bgColor.split(',');
                    const [color1, color2] = gradientColors as string[];
                    carousel.gradientCss = `linear-gradient(180deg, ${color1} 0%, ${color2} 100%)`;
                    carousel.position = index.toString();
                });
                this.couponsCarouselListCopy = this.couponsCarouselList;
                this.slides = this.couponsCarouselList?.length;
                this.sendGTMData('load');
                this.windowRef.nativeWindow.setTimeout(() => {
                    this.isDesktop && this.initShowCarouselButtons();
                }, 2000);
            },
            (error) => {
                console.warn(error);
            },
        );
    }

    /**
     * couponUrl() formation on carousel click
     * @param {ICoupon} coupon
     */
    couponUrl(couponCarousel: ICouponCarousel) {
        const defaultSelectMarket = (this.couponsListService.cmConfigSignal() as ISystemConfig)[couponCarousel.couponKeys];
        const coupon =
            this.couponsList.filter((coupon) => coupon.id === couponCarousel.couponKeys)[0] ||
            ({ id: couponCarousel.couponKeys } as Partial<ICoupon>);
        this.couponsListService.setStoreDefault(coupon, defaultSelectMarket);
        const datedCoupons = (this.couponsListService.getCouponsConfigValue(COUPONS.DATE_COUPONS as keyof ICouponsConfig)() as string).split(',');
        const isinclusionCoupon = this.couponsListService.containsValue(COUPONS.MARKET_INCLUSION as keyof ICouponsConfig, couponCarousel.couponKeys);
        const isExclusionCoupon = this.couponsListService.containsValue(COUPONS.MARKET_UNIQUE_IDS as keyof ICouponsConfig, couponCarousel.couponKeys);
        const redirectCouponId = isinclusionCoupon && !isExclusionCoupon ? datedCoupons[0] : couponCarousel.couponKeys;
        let redirectCouponName = isinclusionCoupon && !isExclusionCoupon ? MATCHES.TODAY : coupon.name;
        const datedCouponNamesSet = new Set(Object.values(DATED_COUPONS));
        if (this.couponsListService.isAdditionalcId(redirectCouponId, 'uniqids')) {
            const [firstWord, ...rest] = redirectCouponName.split(' ');
            if (datedCouponNamesSet.has(firstWord)) {
                redirectCouponName = rest.join(' ');
            }
        }
        let couponName = this.routingHelperService.encodeUrlPart(redirectCouponName);
        if (couponName.includes('btts')) couponName = couponName.replace('btts', 'both-teams-to-score');
        this.sendGTMData('click', couponCarousel);
        this.router.navigate([`/coupons/football/${couponName}/${redirectCouponId}`]);
    }

    public nextSlide(): void {
        this.carousel?.next();
    }

    public prevSlide(): void {
        this.carousel?.previous();
    }
    public get showNext(): boolean {
        return this.carousel.currentSlide < this.carousel.slidesCount - 1;
    }
    public set showNext(value: boolean) {}

    public get showPrev(): boolean {
        return this.carousel.currentSlide > 0;
    }
    public set showPrev(value: boolean) {}

    private get carousel(): Carousel {
        return this.carouselService.get(this.carouselName) || ({} as Carousel);
    }
    private set carousel(value: Carousel) {}

    /**
     * To show/hide carousel butotns
     */
    private initShowCarouselButtons(): void {
        const carouselSlides = this.couponsCarouselList.length;
        const carouselOuterWidth = this.domTools.getWidth(this.elementRef.nativeElement.querySelector('.coupon-carousel'));
        const slideWidth = this.domTools.getWidth(this.elementRef.nativeElement.querySelector('.slide')) + 16;
        this.showCarouselButtons = carouselOuterWidth <= carouselSlides * slideWidth;
        if (!this.showCarouselButtons) {
            this.prevSlide();
        }
    }

    /**
     * Send GA tracking on coupons carousel
     */
    sendGTMData(place: string, carousel?: ICouponCarousel): void {
        const gtmData = {
            'event': place == 'load' ? 'contentView' : 'Event.Tracking',
            'component.CategoryEvent': 'coupons',
            'component.LabelEvent': 'featured coupons',
            'component.ActionEvent': place == 'load' ? 'load' : 'click',
            'component.PositionEvent': 'featured coupons',
            'component.LocationEvent': place == 'load' ? 'featured coupons' : (carousel?.dateBanner ?? ''),
            'component.EventDetails': place == 'load' ? 'coupon carousel' : (carousel?.couponKeys ?? ''),
            'component.URLClicked': 'not applicable',
        };
        if (carousel) {
            gtmData['component.ContentPosition' as keyof typeof gtmData] = carousel.position ?? '';
        }
        this.gtmService.push(gtmData.event, gtmData);
    }

    ngOnDestroy(): void {
        typeof this.resizeListener === 'function' && this.resizeListener();
    }

    /**
     * carousel length
     * @returns - number
     */
    getCarouselLength(): number {
        return this.couponsCarouselList.length;
    }
}
